import React, { useState } from 'react'
import { MoreVertical, Zap, FileText, DollarSign, MessageSquare, Settings } from 'lucide-react'

// Minimal persistent header - ChatGPT/Anthropic style
export default function Header({ 
  onToggleSidebar, 
  onToggleRightSidebar,
  sidebarOpen,
  rightSidebarOpen,
  user, 
  onSignIn,
  isPro = false,
  hasLiveGame = false
}) {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <header 
      className="h-12 px-3 flex items-center justify-between border-b bg-white sticky top-0 z-20"
      style={{ borderColor: '#F3F4F6' }}
    >
      {/* Left: Sidebar toggle + Brand */}
      <div className="flex items-center gap-2">
        <button
          onClick={onToggleSidebar}
          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
            sidebarOpen ? 'bg-gray-100 text-gray-700' : 'hover:bg-gray-100 text-gray-500'
          }`}
          title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          aria-label="Toggle sidebar"
        >
          {/* Panel left icon - modern sidebar toggle */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 3v18" />
          </svg>
        </button>
        
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Orb</span>
          {hasLiveGame && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
              <span className="text-[10px] font-medium text-red-600">LIVE</span>
            </span>
          )}
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-1">
        {/* Pro badge */}
        {isPro && (
          <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
            <Zap size={12} className="text-amber-500" />
            <span className="text-[10px] font-semibold text-amber-700">PRO</span>
          </span>
        )}

        {/* Games toggle - desktop */}
        <button
          onClick={onToggleRightSidebar}
          className={`touch-target-sm hidden md:flex p-2 rounded-lg transition btn-glide ${rightSidebarOpen ? 'bg-pink-50 text-pink-600' : 'hover:bg-gray-100 text-gray-500'}`}
          title={rightSidebarOpen ? 'Hide games' : 'Show games'}
        >
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
          </svg>
        </button>

        {/* Sign in or User menu */}
        {user ? (
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="touch-target-sm p-2 rounded-lg hover:bg-gray-100 transition btn-glide"
            >
              <MoreVertical size={18} className="text-gray-500" />
            </button>
            
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div 
                  className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border py-1 z-50 panel-slide-up"
                  style={{ borderColor: '#E5E7EB' }}
                >
                  <a 
                    href="https://orbglide.com/docs" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition"
                  >
                    <FileText size={14} />
                    Docs
                  </a>
                  <a 
                    href="https://orbglide.com/pricing" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition"
                  >
                    <DollarSign size={14} />
                    Pricing
                  </a>
                  <a 
                    href="https://orbglide.com/feedback" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition"
                  >
                    <MessageSquare size={14} />
                    Feedback
                  </a>
                  <div className="border-t my-1" style={{ borderColor: '#F3F4F6' }} />
                  <button 
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition"
                  >
                    <Settings size={14} />
                    Settings
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <button
            onClick={onSignIn}
            className="touch-target-sm px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition btn-glide"
          >
            Sign in
          </button>
        )}
      </div>
    </header>
  )
}
